import React, { useState, useEffect } from 'react';
import { FormControl, FormControlLabel, Radio, RadioGroup, Typography, Box, Alert, Grid } from '@mui/material';
import { updateUserAttributes } from 'aws-amplify/auth';
import { useThemeContext } from '../../contexts/ThemeContext';
import InfoIcon from '@mui/icons-material/Info';

const ProfileThemeToggle: React.FC = () => {
  const { cognitoUserInfo, updateCognitoUserInfo } = useThemeContext();
  const themeValue = cognitoUserInfo.attributes['custom:theme'] || 'light';
  const [selectedTheme, setSelectedTheme] = useState<string>(themeValue);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  useEffect(() => {
    if (cognitoUserInfo && cognitoUserInfo.attributes['custom:theme']) {
      setSelectedTheme(cognitoUserInfo.attributes['custom:theme']);
    }
  }, [cognitoUserInfo]);

  const handleThemeChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newTheme = event.target.value;
    setSelectedTheme(newTheme);
    setIsUpdating(true);
    setUpdateError(null);

    try {
      // update cognito user with new theme preference selected
      await updateUserAttributes({
        userAttributes: {
          'custom:theme': newTheme,
        },
      });
      // in order for us to trigger real time updates for the theme based on preferences
      updateCognitoUserInfo({
        ...cognitoUserInfo,
        attributes: { ...cognitoUserInfo.attributes, 'custom:theme': newTheme },
      });
    } catch (error) {
      setUpdateError('Failed to update theme preference. Please try again.');
      console.error('Error updating theme preference:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box sx={{ p: 5 }}>
      <Grid container spacing={2}>
        <Grid xs={12}>
          <Typography variant="h6" gutterBottom>
            Appearance
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }} icon={<InfoIcon />}>
            <Typography variant="body2">
              Light mode is in beta. You may notice some minor inconsistencies but we are working to fix these.
            </Typography>
          </Alert>

          {updateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {updateError}
            </Alert>
          )}

          <Typography variant="body2" gutterBottom>
            Choose if you want the app to appear in dark mode, light mode, or follow your computer's settings.
          </Typography>

          <FormControl component="fieldset">
            <RadioGroup aria-label="theme" name="theme-selection" value={selectedTheme} onChange={handleThemeChange}>
              <FormControlLabel
                value="light"
                control={<Radio disabled={isUpdating} checked={selectedTheme === 'light'} />}
                label="Light"
              />
              <FormControlLabel
                value="dark"
                control={<Radio disabled={isUpdating} checked={selectedTheme === 'dark'} />}
                label="Dark"
              />

              <FormControlLabel
                value="system"
                control={<Radio disabled={isUpdating} checked={selectedTheme === 'system'} />}
                label="Auto"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfileThemeToggle;
